import { MapContainer, ImageOverlay, useMap, Marker } from "react-leaflet";
import { type FC, useEffect, useMemo, useRef, useState } from "react";
import L, { CRS, divIcon, type DivIcon, type IconOptions } from "leaflet";
import "leaflet/dist/leaflet.css";
import type { MarkerFormData } from "@/components/Leaflet/ImageMapLeafletImageLoader";
import shadowUrl from "leaflet/dist/images/marker-shadow.png";
import markerUrl from "leaflet/dist/images/marker-icon.png";

// Build a DivIcon that shows the default pin plus your number
export function createNumberedDivIcon(num: number): DivIcon {
  const html = `
    <div class="numbered-pin">
      <img src="${markerUrl}" alt="marker"/>
      <span class="pin-number">${num}</span>
    </div>
  `;

  const options: IconOptions = {
    iconUrl: markerUrl,
    iconRetinaUrl: markerUrl,
    shadowUrl: shadowUrl,
    html,
    className: "",
    iconSize: [25, 41],
    iconAnchor: [12, 41],

    shadowSize: [41, 41],
    shadowAnchor: [12, 41],
  };

  return divIcon(options);
}

type MarkerPositionLatLng = { lng: number; lat: number };

const DEFAULT_IMAGE_HEIGHT = 300;
const DEFAULT_IMAGE_WIDTH = 300;

interface DraggableMarkerProps {
  initialPosition: MarkerPositionLatLng;
  markerIndex: number;
  imgSize: { height: number; width: number };
  handleMarkerChange: (idx: number, position: MarkerPositionLatLng) => void;
}

const DraggableMarker: FC<DraggableMarkerProps> = ({ initialPosition, markerIndex, imgSize, handleMarkerChange }) => {
  const [position, setPosition] = useState(initialPosition);
  const markerRef = useRef(null);
  useEffect(() => {
    setPosition(initialPosition);
  }, [initialPosition]);

  const eventHandlers = useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current;
        if (marker != null) {
          const _position = { ...marker.getLatLng() };
          _position.lat = Math.min(Math.max(_position.lat, 0), imgSize.height);
          _position.lng = Math.min(Math.max(_position.lng, 0), imgSize.width);

          setPosition(_position);
          handleMarkerChange(markerIndex, _position);
        }
      },
    }),
    [markerIndex, handleMarkerChange]
  );

  return (
    <Marker
      icon={createNumberedDivIcon(markerIndex + 1)}
      draggable={true}
      eventHandlers={eventHandlers}
      position={position}
      ref={markerRef}
    ></Marker>
  );
};

export function FitBounds({ bounds }: { bounds: L.LatLngBounds }) {
  const map = useMap();

  useEffect(() => {
    const container = map.getContainer();
    let io: IntersectionObserver;

    // only call fit once
    const attemptFit = () => {
      const rect = container.getBoundingClientRect();
      const { x, y } = map.getSize();
      const sw = bounds.getSouthWest(),
        ne = bounds.getNorthEast();

      // 1) must have a real on‐screen size
      if (rect.width <= 0 || rect.height <= 0) return;
      // 2) bounds must be finite
      if (![sw.lat, sw.lng, ne.lat, ne.lng].every(Number.isFinite)) return;

      map.invalidateSize();
      map.fitBounds(bounds, { animate: false });

      // cleanup
      io.disconnect();
    };

    io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          attemptFit();
        }
      },
      { threshold: 0.01 } // fire as soon as at least 1% is visible
    );
    io.observe(container);

    return () => {
      io.disconnect();
    };
  }, [map, bounds]);

  return null;
}

interface ImageMapLeafletProps {
  imgUrl: string;
  width?: number;
  height?: number;
  markers: MarkerFormData[];
  setMarkers: any;
}

const MAX_BOUNDS_PADDING = 50;

const ImageMapLeaflet: FC<ImageMapLeafletProps> = ({ imgUrl, width, height, markers, setMarkers }) => {
  if (!Number.isFinite(width!) || !Number.isFinite(height!)) {
    return <div>Loading…</div>;
  }
  const bounds = useMemo(
    () =>
      L.latLngBounds(
        [-MAX_BOUNDS_PADDING, -MAX_BOUNDS_PADDING],
        [height! + MAX_BOUNDS_PADDING, width! + MAX_BOUNDS_PADDING]
      ),
    [width, height]
  );

  const imgBounds = useMemo(() => L.latLngBounds([0, 0], [height!, width!]), [width, height]);

  const handleMarkerChange = (idx: number, position: MarkerPositionLatLng) => {
    const relativePosition = {
      x: (position.lng / width!) * 100,
      y: (position.lat / height!) * 100,
    };

    setMarkers([
      ...markers.slice(0, idx),
      { ...markers[idx], coordinates: relativePosition },
      ...markers.slice(idx + 1),
    ]);
  };

  return (
    <MapContainer
      crs={CRS.Simple}
      style={{ width: "100%", height: 500 }}
      bounds={bounds}
      minZoom={-3}
      zoomSnap={0.1}
      zoomDelta={0.5}
      scrollWheelZoom={true}
      wheelPxPerZoomLevel={120}
    >
      <FitBounds bounds={bounds} />

      <ImageOverlay url={imgUrl} bounds={imgBounds} />
      {!!markers &&
        markers.map((m, idx) => {
          return (
            <DraggableMarker
              key={`draggableMarker_${idx}_${m.document_id ?? m.to_document_id}`}
              markerIndex={idx}
              initialPosition={{
                lng: (width ?? DEFAULT_IMAGE_WIDTH) * (m.coordinates.x / 100),
                lat: (height ?? DEFAULT_IMAGE_HEIGHT) * (m.coordinates.y / 100),
              }}
              imgSize={{ height: height!, width: width! }}
              handleMarkerChange={handleMarkerChange}
            />
          );
        })}
    </MapContainer>
  );
};

export default ImageMapLeaflet;
